import { DateTime } from "luxon";

import { IPartImage } from "../models/Part";

type OrderableImage = Pick<IPartImage, "id" | "order" | "created" | "imagedOn">;

// When the photo was taken, falling back to when the row was made, as the API does. Unparseable
// dates go last rather than throwing the rest of the order out.
function takenAt(image: OrderableImage): number {
  const taken = DateTime.fromISO(image.imagedOn ?? image.created ?? "", { zone: "utc" });
  return taken.isValid ? taken.toMillis() : Number.MAX_SAFE_INTEGER;
}

/**
 * The order a part's images are shown in: the numbered ones first, in their listing order, then the
 * unnumbered ones in the order they were taken. Returns a new array - callers pass the React Query
 * cache, which must not be sorted in place.
 */
export function sortPartImages<T extends OrderableImage>(images: T[] | null | undefined): T[] {
  return [...(images ?? [])].sort((a, b) => {
    const aNumbered = a.order != null;
    const bNumbered = b.order != null;

    if (aNumbered !== bNumbered) return aNumbered ? -1 : 1;
    if (aNumbered && a.order !== b.order) return (a.order ?? 0) - (b.order ?? 0);

    return takenAt(a) - takenAt(b) || a.id.localeCompare(b.id);
  });
}
